import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CreditCard, QrCode, Power, BellOff, Rocket } from "lucide-react";

const STEPS = [
  {
    num: "1️⃣",
    icon: CreditCard,
    title: "Passo 1: Escolha o seu Plano 💳",
    lines: [
      'Vá na aba de planos e assine a quantidade de chips que você quer proteger.',
      "Assim que o Pix for pago, suas vagas de chips serão liberadas na hora!",
    ],
    cta: { to: "/planos", label: "Ver planos", testid: "guia-step1-cta" },
    testid: "guia-step-1",
  },
  {
    num: "2️⃣",
    icon: QrCode,
    title: "Passo 2: Conecte o seu WhatsApp 📲",
    lines: [
      'Clique no botão verde "Adicionar Novo Chip".',
      "Um código quadrado (QR Code) vai aparecer na sua tela.",
      "Pegue o seu celular real, abra o WhatsApp, vá em Configurações > Aparelhos Conectados e clique em Conectar um Aparelho.",
      "Aponte a câmera do seu celular para a tela do computador e leia o código quadrado.",
    ],
    cta: { to: "/dashboard", label: "Conectar meu chip", testid: "guia-step2-cta" },
    testid: "guia-step-2",
  },
  {
    num: "3️⃣",
    icon: Power,
    title: "Passo 3: Ative a Mágica! ✨",
    lines: [
      'Ligue a chavinha escrito "Ativar Aquecimento".',
      "Pronto! O nosso robô vai começar a conversar na nuvem sozinho.",
    ],
    cta: { to: "/aquecimento", label: "Ativar aquecimento", testid: "guia-step3-cta" },
    testid: "guia-step-3",
  },
];

export default function GuiaRapido() {
  return (
    <div className="space-y-8 max-w-3xl mx-auto" data-testid="guia-page">
      <div className="text-center animate-fade-up">
        <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4 shadow-[0_0_30px_rgba(16,185,129,0.25)]">
          <Rocket className="w-7 h-7 text-emerald-400" />
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">
          Bem-vindo ao MaturaMax! 🎉
        </h1>
        <p className="text-sm sm:text-base text-slate-400 mt-2">
          Aqui você blinda o seu WhatsApp em 3 passos simples. Siga o guia:
        </p>
      </div>

      <div className="relative space-y-6">
        <div className="absolute left-[27px] top-8 bottom-8 w-px bg-gradient-to-b from-emerald-500/50 via-emerald-500/20 to-transparent hidden sm:block" />
        {STEPS.map((step, i) => (
          <div
            key={step.testid}
            data-testid={step.testid}
            className="relative bg-[#161F32] border border-slate-800 rounded-xl p-5 sm:p-6 transition-all duration-200 hover:border-emerald-500/30 hover:shadow-[0_0_25px_rgba(16,185,129,0.1)] animate-fade-up"
            style={{ animationDelay: `${i * 120}ms` }}
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 shrink-0 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-2xl relative z-10">
                {step.num}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2.5">
                  <step.icon className="w-5 h-5 text-emerald-400 shrink-0" />
                  <h2 className="font-heading text-lg font-bold text-slate-100">{step.title}</h2>
                </div>
                <ul className="space-y-1.5">
                  {step.lines.map((line) => (
                    <li key={line} className="text-sm text-slate-400 leading-relaxed flex gap-2">
                      <span className="text-emerald-400 mt-0.5 shrink-0">•</span>
                      {line}
                    </li>
                  ))}
                </ul>
                <Link to={step.cta.to} className="inline-block mt-4">
                  <Button size="sm" data-testid={step.cta.testid}
                    className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold h-9 text-xs">
                    {step.cta.label}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 sm:p-6 animate-fade-up" style={{ animationDelay: "380ms" }} data-testid="guia-cloud-note">
        <p className="text-sm sm:text-base text-slate-200 leading-relaxed">
          🚫 <strong>Você já pode desligar o seu computador e o seu celular!</strong> O MaturaMax continua trabalhando para você 24 horas por dia, mesmo com tudo desligado.
        </p>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5 sm:p-6 flex items-start gap-3 animate-fade-up" style={{ animationDelay: "460ms" }} data-testid="guia-golden-tip">
        <BellOff className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <p className="text-sm text-slate-300 leading-relaxed">
          <strong className="text-amber-300">💡 Dica de Ouro:</strong> Silencie as notificações dessas conversas no seu celular para o aparelho não ficar tocando o dia todo enquanto os robôs conversam!
        </p>
      </div>
    </div>
  );
}
